import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { AuthService as Auth0Service } from '@auth0/auth0-angular';
import { combineLatest, from, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { ConfigService } from '../config.service';
import { AuthService } from '../auth.service';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  constructor(
    private auth0: Auth0Service,
    private auth: AuthService,
    private config: ConfigService,
    private router: Router,
  ) { }

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.redirectIfUnauthenticated(state);
  }

  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.redirectIfUnauthenticated(state);
  }

  private redirectIfUnauthenticated(state: RouterStateSnapshot): Observable<boolean> {

    this.auth.resetSessionTimeout(); // mark our session as active

    if (state.url === '/logout') {
      return from(this.auth.logout()).pipe(map(() => false));
    }

    if (!this.config.isAuth0Enabled()) {
      if (!this.auth.isLoggedIn) {
        this.router.navigate(['/login'], { queryParams: { redirect: state.url } });
      }
      return of(true);
    }

    // TODO: Evaluate this improvement from claude when we aren't in a release window
    // according to claude...
    // auth0's silent-auth (checkSession) uses web_message/iframe and can silently hang
    // for up to 60s if the /authorize endpoint returns 400. Bail out early so the guard
    // doesn't leave the user stuck with an empty center pane.
    /*
    return combineLatest([this.auth0.isAuthenticated$, this.auth0.idTokenClaims$]).pipe(
      timeout(10000),
      catchError(() => of([false, null] as const)),
      switchMap(([loggedIn, idToken]) => {

        const tokenIsExpired = idToken ? idToken.exp * 1000 < Date.now() : false;

        if (!loggedIn || !idToken || tokenIsExpired) {
          return this.auth0.loginWithRedirect({
            appState: { target: state.url },
            authorizationParams: {
              prompt: 'login',
            },
          }).pipe(
            catchError(() => {
              // loginWithRedirect can fail if auth0 is in a bad state after a failed
              // silent-auth attempt. Hard-reload to clear any corrupted SDK state.
              window.location.assign(window.location.origin);
              return of(undefined);
            }),
            map(() => false),
          );
        }
        else {
          this.auth.storeAuth0Token();
          return of(true);
        }
      }),
    );
    */

    return combineLatest([this.auth0.isAuthenticated$, this.auth0.idTokenClaims$]).pipe(
      switchMap(([loggedIn, idToken]) => {

        const tokenIsExpired = idToken ? idToken.exp * 1000 < Date.now() : false;

        if (!loggedIn || !idToken || tokenIsExpired) {
          this.auth0.loginWithRedirect({
            appState: { target: state.url },
            authorizationParams: {
              prompt: 'login',
            },
          });

          return of(false);
        }
        else {
          this.auth.storeAuth0Token();
          return of(true);
        }
      }),
    );
  }
}
